import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import useTheme from '../../ColorConfig/usetheme';
import ServicesHeader from './serviceheader';
import DesktopServicesGrid from './desktopservice';
import MobileServicesStack from './mobileservice';
import LoadingState from './loadingstate';
import ErrorState from './errorstate';
import './coreservices.css';

const CoreServices = ({ onNavigateToService }) => {
  const colors = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const sectionRef = useRef(null);

  // Check if device is mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch services data from API
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://api.dremerz.net/api/DZ_8923_Idlbay_Services_ServicesList');
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Transform API data to match the expected format
        const transformedData = data.map(service => ({
          id: parseInt(service.id),
          title: service.title,
          description: service.description,
          image: service.media ? `https://api.dremerz.net/uploads/${service.media}` : "https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80",
          fullDescription: service.fullDescription,
          features: service.features ? JSON.parse(service.features) : [],
          benefits: service.benefits ? JSON.parse(service.benefits) : [],
          category: service.category || "General"
        }));
        
        setServicesData(transformedData);
        setError(null);
      } catch (error) {
        console.error('Error fetching services:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  useEffect(() => {
    if (location.state && location.state.scrollToServices) {
      setTimeout(() => {
        const element = document.getElementById('services');
        if (element) {
          const headerHeight = document.querySelector('.navbar-custom')?.offsetHeight || 0;
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerHeight;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 100);
    }
  }, [location]);

  const handleServiceClick = (serviceId) => {
    const service = servicesData.find(s => s.id === serviceId);
    
    if (service) {
      navigate(`/servicedetails/${serviceId}`, { 
        state: { 
          serviceData: service
        } 
      });
    }
  };

  const handleViewAllServices = () => {
    navigate('/services');
  };

  // Loading state
  if (loading) {
    return <LoadingState />;
  }

  // Error state
  if (error) {
    return <ErrorState error={error} />;
  }

  const displayServices = servicesData.slice(0, 6);

  return (
    <section id="services" ref={sectionRef} className="services-section py-1" style={{ backgroundColor: `var(--color-bodyBlack)`}}>
      <div className="container">
        <ServicesHeader />
        
        {/* Mobile Overlapping Stack View */}
        {isMobile && (
          <MobileServicesStack 
            services={displayServices}
            onServiceClick={handleServiceClick}
          />
        )}

        {/* Desktop Grid View */}
        {!isMobile && (
          <DesktopServicesGrid 
            services={displayServices}
            onServiceClick={handleServiceClick}
          />
        )}

        {servicesData.length > 6 && (
          <motion.div 
            className="row"
            initial={{ opacity: 0, scale: 0.8 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.9 }}
          >
            <div className="col-12 text-center">
              <button 
                className="view-all-btn1"
                onClick={handleViewAllServices}
              >
                View All Services →
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default CoreServices;